import { Select, TextInput } from 'react-hook-form-mantine';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Stack, Group, Flex, Button } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import {
	UPDATE_ORGANIZATION_FORM_FIELDS,
	UpdateOrganizationSettingsInputsValues,
	UpdateOrganizationShape,
} from '@dealstack/frontend/app/pages/Organization/OrganizationGeneralSettings/OrganizationGeneralSettings.types';
import { ORGANIZATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { OrganizationType } from '@dealstack/shared/lib/generated';

interface Props {
	name: string;
	contactEmail?: string;
	type: OrganizationType | null;
	showType: boolean;
	canEditType: boolean;
	isLoading: boolean;
	handleUpdateOrganization: (formData: UpdateOrganizationSettingsInputsValues) => void;
}

export const OrganizationForm = ({
	name,
	contactEmail,
	type,
	showType,
	canEditType,
	isLoading,
	handleUpdateOrganization,
}: Props) => {
	const tOrganization = useTranslationOrganization();

	const updateOrganizationForm = useForm({
		mode: 'all',
		resolver: zodResolver(UpdateOrganizationShape()),
		defaultValues: {
			[UPDATE_ORGANIZATION_FORM_FIELDS.NAME]: name,
			[UPDATE_ORGANIZATION_FORM_FIELDS.CONTACT_EMAIL]: contactEmail,
			[UPDATE_ORGANIZATION_FORM_FIELDS.TYPE]: type,
		},
	});

	const typeOptions = Object.values(OrganizationType).map((organizationType) => ({
		value: organizationType,
		label: tOrganization(`general_form.type_field_option.${organizationType}`),
	}));

	return (
		<Flex direction="column" justify="space-between" h="100%" style={{ flex: 1 }}>
			<ContainerWithHeader
				title={tOrganization('general_form.title')}
				headerActions={
					<Button
						type="submit"
						data-testid={ORGANIZATION_ELEMENT_ID.GENERAL_SETTINGS_UPDATE_BUTTON}
						disabled={
							!updateOrganizationForm.formState.isDirty ||
							!updateOrganizationForm.formState.isValid ||
							isLoading
						}
						onClick={updateOrganizationForm.handleSubmit((formData) =>
							handleUpdateOrganization(formData)
						)}
					>
						{tOrganization('general_form.button')}
					</Button>
				}
			>
				<FormProvider {...updateOrganizationForm}>
					<form>
						<Stack gap="md">
							<Group align="flex-start" grow>
								<Stack gap="md" style={{ flex: 1 }}>
									<TextInput
										data-testid={
											ORGANIZATION_ELEMENT_ID.GENERAL_SETTINGS_NAME_INPUT
										}
										label={tOrganization('general_form.name_field_label')}
										name={UPDATE_ORGANIZATION_FORM_FIELDS.NAME}
										size="md"
									/>
								</Stack>
								<Stack gap="md" style={{ flex: 1 }}>
									<TextInput
										data-testid={
											ORGANIZATION_ELEMENT_ID.GENERAL_SETTINGS_CONTACT_EMAIL_INPUT
										}
										label={tOrganization('general_form.contact_email_label')}
										name={UPDATE_ORGANIZATION_FORM_FIELDS.CONTACT_EMAIL}
										size="md"
									/>
								</Stack>
							</Group>
							{showType && (
								<Group align="flex-start" grow>
									<Stack gap="md" style={{ flex: 1 }}>
										<Select
											data-testid={
												ORGANIZATION_ELEMENT_ID.GENERAL_SETTINGS_TYPE_INPUT
											}
											label={tOrganization('general_form.type_field_label')}
											name={UPDATE_ORGANIZATION_FORM_FIELDS.TYPE}
											data={typeOptions}
											disabled={!canEditType}
											clearable={canEditType}
											size="md"
											onChange={(value) =>
												updateOrganizationForm.setValue(
													UPDATE_ORGANIZATION_FORM_FIELDS.TYPE,
													Object.values(OrganizationType).find(
														(organizationType) =>
															organizationType === value
													) ?? null,
													{ shouldDirty: true, shouldValidate: true }
												)
											}
										/>
									</Stack>
								</Group>
							)}
						</Stack>
					</form>
				</FormProvider>
			</ContainerWithHeader>
		</Flex>
	);
};
